import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { WS_BASE, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// ICE configuration duplicated from StreamPlayer so the provider is self-contained.
const ICE_SERVERS = (() => {
  const base = [
    { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302", "stun:stun2.l.google.com:19302"] },
  ];
  const turnUrl = process.env.REACT_APP_TURN_URL;
  if (turnUrl) {
    base.push({
      urls: turnUrl.split(",").map(s => s.trim()),
      username: process.env.REACT_APP_TURN_USERNAME || "",
      credential: process.env.REACT_APP_TURN_CREDENTIAL || "",
    });
  }
  return base;
})();

const StreamingCtx = createContext(null);

/**
 * StreamingProvider — global (App-level) state for an authenticated user's
 * OUTGOING broadcast. It owns:
 *   • The channel WebSocket (single long-lived connection)
 *   • The captured MediaStream (camera / mic / screen)
 *   • The per-viewer RTCPeerConnection map
 *
 * Because it lives above <Routes>, it survives route changes — the streamer
 * can navigate to /games/slots (or anywhere else) and stay live. A small
 * floating preview (<StreamingBubble />) shows the local video and controls
 * when they're off their own channel page.
 */
export function StreamingProvider({ children }) {
  const { user } = useAuth();

  // Public reactive state
  const [isLive, setIsLive] = useState(false);
  const [channelUsername, setChannelUsername] = useState(null);
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [screenShare, setScreenShare] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [peerId, setPeerId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [wsReady, setWsReady] = useState(false);

  // Non-reactive refs (WebRTC + WS state)
  const streamRef = useRef(null);
  const wsRef = useRef(null);
  const peersRef = useRef({}); // viewerPeerId -> RTCPeerConnection
  const previewElsRef = useRef(new Set()); // <video> elements to attach stream to
  const closingRef = useRef(false);
  const reconnectRef = useRef({ attempts: 0, timer: null });
  // Snapshot loop — periodically captures a small JPEG from the outgoing
  // stream and POSTs it so the landing page hero widget can preview us
  // without every visitor spinning up their own RTCPeerConnection.
  const snapshotRef = useRef({ timer: null, canvas: null, video: null });

  // --- Screen + Camera compositor (Picture-in-Picture) ---
  // When the streamer chooses "Screen + Camera", we composite the two feeds
  // client-side onto an offscreen canvas: screen fills the frame, camera is
  // drawn as a movable/resizable PiP overlay. Canvas.captureStream() is the
  // outgoing video track. Screen tab audio + mic are mixed via WebAudio.
  const compositeRef = useRef(null); // { canvas, ctx, screenVideo, camVideo, rafId, audioCtx, sourceStreams }
  const [cameraOverlay, setCameraOverlay] = useState({ x: 0.7, y: 0.7, w: 0.28, h: 0.28, mirror: true });
  const [pipMode, setPipMode] = useState(false); // true when active screen+camera composite is running
  const cameraOverlayRef = useRef(cameraOverlay);
  useEffect(() => { cameraOverlayRef.current = cameraOverlay; }, [cameraOverlay]);

  // --- Recording ---
  // We use MediaRecorder against the persistent MediaStream so the recording
  // survives every route change (the provider outlives the router). Chunks
  // accumulate in memory and get sealed into a Blob when the streamer clicks
  // End Stream — a review modal then offers Save / Download / Delete.
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordStartRef = useRef(null); // wall-clock start (ms)
  const [pendingRecording, setPendingRecording] = useState(null); // { blob, url, sizeBytes, durationMs, channelUsername } or null
  const [recordingSupported, setRecordingSupported] = useState(true);
  // Broadcast a plain WS message-event subscription for consumers (Channel.jsx
  // subscribes for chat, guest-request signaling, etc. when the current
  // channel matches the streaming channel).
  const messageListenersRef = useRef(new Set());

  const emitMessage = (evt) => {
    messageListenersRef.current.forEach(fn => {
      try { fn(evt); } catch { /* noop */ }
    });
  };
  const subscribeMessages = useCallback((fn) => {
    messageListenersRef.current.add(fn);
    return () => messageListenersRef.current.delete(fn);
  }, []);

  const attachPreview = useCallback((el) => {
    if (!el) return () => {};
    previewElsRef.current.add(el);
    if (streamRef.current) el.srcObject = streamRef.current;
    return () => {
      previewElsRef.current.delete(el);
      try { if (el.srcObject) el.srcObject = null; } catch { /* noop */ }
    };
  }, []);

  const applyStreamToPreviews = (stream) => {
    previewElsRef.current.forEach(el => {
      try { el.srcObject = stream; } catch { /* noop */ }
    });
  };

  const sendWs = useCallback((obj) => {
    const sock = wsRef.current;
    if (sock && sock.readyState === 1) {
      try { sock.send(typeof obj === "string" ? obj : JSON.stringify(obj)); } catch { /* noop */ }
      return true;
    }
    return false;
  }, []);

  // Guests currently on-air with this streamer. Populated when a viewer
  // sends an offer with role="guest" AFTER being approved via guest_accept.
  // Exposed as reactive `guests` for the StreamPlayer to render guest tiles.
  const guestPcsRef = useRef({}); // peer_id -> RTCPeerConnection
  const [guests, setGuests] = useState({}); // peer_id -> { username, stream }
  const guestUsernameRef = useRef({}); // peer_id -> username (cached from guest_request)

  // ===== Internal: streamer-side WebRTC signal handler =====
  const handleSignal = async (msg) => {
    if (msg.type !== "signal") return;
    const fromPeer = msg.from;
    const data = msg.data;
    if (!streamRef.current) return;

    // ---- Guest inbound (role=guest offer) ----
    // A viewer whose guest_request was approved is now publishing THEIR camera
    // + mic to us. We create a distinct PC so we don't tangle their tracks
    // with our outbound viewer PCs. Then we broadcast `guest_live` so all
    // viewers know to open their own PC to the guest's peer_id.
    if (data?.type === "offer" && data?.role === "guest") {
      let gpc = guestPcsRef.current[fromPeer];
      if (gpc) { try { gpc.close(); } catch { /* noop */ } }
      gpc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      guestPcsRef.current[fromPeer] = gpc;
      const guestStream = new MediaStream();
      gpc.ontrack = (ev) => {
        guestStream.addTrack(ev.track);
        setGuests((g) => ({
          ...g,
          [fromPeer]: { ...g[fromPeer], username: g[fromPeer]?.username || guestUsernameRef.current[fromPeer] || "guest", stream: guestStream },
        }));
      };
      gpc.oniceconnectionstatechange = () => {
        const s = gpc.iceConnectionState;
        if (s === "failed" || s === "closed" || s === "disconnected") {
          try { gpc.close(); } catch { /* noop */ }
          delete guestPcsRef.current[fromPeer];
          setGuests((g) => { const c = { ...g }; delete c[fromPeer]; return c; });
          sendWs({ type: "guest_offline", guest_peer: fromPeer });
        }
      };
      gpc.onicecandidate = (ev) => {
        if (ev.candidate) sendWs({ type: "signal", to: fromPeer, data: { type: "ice", candidate: ev.candidate } });
      };
      await gpc.setRemoteDescription(new RTCSessionDescription(data));
      const answer = await gpc.createAnswer();
      await gpc.setLocalDescription(answer);
      sendWs({ type: "signal", to: fromPeer, data: { type: "answer", sdp: answer.sdp } });
      // Tell everyone in the room that a guest is now on-air so viewers
      // can create a direct PC to the guest's peer_id.
      const gu = data.guest_username || guestUsernameRef.current[fromPeer] || "guest";
      guestUsernameRef.current[fromPeer] = gu;
      sendWs({ type: "guest_live", guest_peer: fromPeer, guest_username: gu });
      return;
    }

    // ---- Guest ICE / answer (handshake back-and-forth) ----
    if (guestPcsRef.current[fromPeer]) {
      const gpc = guestPcsRef.current[fromPeer];
      if (data?.type === "ice") {
        try { await gpc.addIceCandidate(data.candidate); } catch { /* noop */ }
      } else if (data?.type === "answer") {
        try { await gpc.setRemoteDescription(new RTCSessionDescription(data)); } catch { /* noop */ }
      }
      return;
    }

    // ---- Standard viewer PC path (streamer → viewer) ----
    let pc = peersRef.current[fromPeer];
    if (!pc && data?.type === "offer") {
      pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peersRef.current[fromPeer] = pc;
      pc.oniceconnectionstatechange = () => {
        const s = pc.iceConnectionState;
        if (s === "failed" || s === "closed" || s === "disconnected") {
          try { pc.close(); } catch { /* noop */ }
          delete peersRef.current[fromPeer];
        }
      };
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => pc.addTrack(t, streamRef.current));
      }
      pc.onicecandidate = (ev) => {
        if (ev.candidate) sendWs({ type: "signal", to: fromPeer, data: { type: "ice", candidate: ev.candidate } });
      };
      await pc.setRemoteDescription(new RTCSessionDescription(data));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendWs({ type: "signal", to: fromPeer, data: { type: "answer", sdp: answer.sdp } });
    } else if (pc && data?.type === "ice") {
      try { await pc.addIceCandidate(data.candidate); } catch { /* noop */ }
    } else if (pc && data?.type === "answer") {
      try { await pc.setRemoteDescription(new RTCSessionDescription(data)); } catch { /* noop */ }
    }
  };

  const openWs = (ch, jwt) =>
    new Promise((resolve, reject) => {
      const url = `${WS_BASE}/api/ws/${ch}${jwt ? `?token=${encodeURIComponent(jwt)}` : ""}`;
      const sock = new WebSocket(url);
      let settled = false;
      sock.onopen = () => {
        sock.send(JSON.stringify({ type: "join", username: user?.username || "Guest", role: "streamer" }));
        setWsReady(true);
        if (!settled) { settled = true; resolve(sock); }
      };
      sock.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "welcome") setPeerId(msg.peer_id);
          if (msg.type === "presence") setViewerCount(msg.count || 0);
          if (msg.type === "signal") handleSignal(msg);
        } catch { /* noop */ }
        emitMessage(e);
      };
      sock.onerror = () => { if (!settled) { settled = true; reject(new Error("WebSocket error")); } };
      sock.onclose = () => {
        setWsReady(false);
        // Auto-reconnect if the stream is still supposed to be live.
        // Exponential backoff (1.5s → 3s → 6s → 12s → 20s), capped, with
        // a max attempt ceiling so a persistently failing WS doesn't burn
        // battery. If we exceed the cap, we surface a toast and give up.
        if (!closingRef.current && streamRef.current) {
          const attempts = reconnectRef.current.attempts + 1;
          reconnectRef.current.attempts = attempts;
          if (attempts > 8) {
            toast.error("Lost connection to the stream server", {
              description: "Please refresh and go live again.",
            });
            return;
          }
          const delay = Math.min(1500 * Math.pow(2, attempts - 1), 20000);
          if (reconnectRef.current.timer) clearTimeout(reconnectRef.current.timer);
          reconnectRef.current.timer = setTimeout(() => {
            if (streamRef.current && !closingRef.current) {
              openWs(ch, jwt).then((s) => {
                wsRef.current = s;
                reconnectRef.current.attempts = 0;
                try { s.send(JSON.stringify({ type: "streamer_live" })); } catch { /* noop */ }
              }).catch(() => {});
            }
          }, delay);
        }
      };
    });

  // ---- Compositor: screen + camera picture-in-picture ----
  const buildComposite = async ({ cameraId, micId } = {}) => {
    // 1) Grab the raw streams
    const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    let camStream = null;
    try {
      camStream = await navigator.mediaDevices.getUserMedia({
        video: cameraId ? { deviceId: { exact: cameraId }, width: { ideal: 640 }, height: { ideal: 480 } } : { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
    } catch (e) {
      // Camera failed — tear down screen and re-throw so caller can surface a clear error.
      try { screenStream.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
      throw e;
    }
    let micStream = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: micId ? { deviceId: { exact: micId } } : true, video: false,
      });
    } catch { /* noop — proceed without mic */ }

    // 2) Wire up offscreen video elements
    const screenVideo = document.createElement("video");
    screenVideo.srcObject = screenStream; screenVideo.autoplay = true; screenVideo.muted = true; screenVideo.playsInline = true;
    await screenVideo.play().catch(() => {});
    const camVideo = document.createElement("video");
    camVideo.srcObject = camStream; camVideo.autoplay = true; camVideo.muted = true; camVideo.playsInline = true;
    await camVideo.play().catch(() => {});

    // 3) Canvas — 1280x720 output frame
    const canvas = document.createElement("canvas");
    canvas.width = 1280; canvas.height = 720;
    const ctx = canvas.getContext("2d", { alpha: false });

    // Render loop — draws screen + camera at current overlay coordinates.
    // cameraOverlayRef is a live ref updated by React state so dragging the
    // preview updates the outgoing stream in real time.
    let stopped = false;
    let rafId = null;
    const draw = () => {
      if (stopped) return;
      rafId = requestAnimationFrame(draw);
      if (compositeRef.current) compositeRef.current.rafId = rafId;
      const sw = screenVideo.videoWidth || 1280, sh = screenVideo.videoHeight || 720;
      // Contain screen inside 1280x720
      const scale = Math.min(canvas.width / sw, canvas.height / sh);
      const drawW = sw * scale, drawH = sh * scale;
      const drawX = (canvas.width - drawW) / 2, drawY = (canvas.height - drawH) / 2;
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      try { ctx.drawImage(screenVideo, drawX, drawY, drawW, drawH); } catch { /* not ready yet */ }
      // Camera PiP overlay (position/size are 0..1 fractions of canvas)
      const ov = cameraOverlayRef.current;
      if (ov && camVideo.videoWidth) {
        const ox = Math.max(0, Math.min(canvas.width - 40, ov.x * canvas.width));
        const oy = Math.max(0, Math.min(canvas.height - 40, ov.y * canvas.height));
        const ow = Math.max(80, Math.min(canvas.width - ox, ov.w * canvas.width));
        const oh = Math.max(60, Math.min(canvas.height - oy, ov.h * canvas.height));
        // Rounded rect clip + neon border
        const radius = Math.min(16, ow / 8);
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(ox + radius, oy);
        ctx.arcTo(ox + ow, oy,       ox + ow, oy + oh, radius);
        ctx.arcTo(ox + ow, oy + oh,  ox,      oy + oh, radius);
        ctx.arcTo(ox,      oy + oh,  ox,      oy,      radius);
        ctx.arcTo(ox,      oy,       ox + ow, oy,      radius);
        ctx.closePath();
        ctx.clip();
        if (ov.mirror) {
          ctx.translate(ox + ow, oy);
          ctx.scale(-1, 1);
          try { ctx.drawImage(camVideo, 0, 0, ow, oh); } catch { /* noop */ }
        } else {
          try { ctx.drawImage(camVideo, ox, oy, ow, oh); } catch { /* noop */ }
        }
        ctx.restore();
        // Neon border
        ctx.strokeStyle = "#00E5FF";
        ctx.lineWidth = 3;
        ctx.shadowColor = "#00E5FF";
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(ox + radius, oy);
        ctx.arcTo(ox + ow, oy,       ox + ow, oy + oh, radius);
        ctx.arcTo(ox + ow, oy + oh,  ox,      oy + oh, radius);
        ctx.arcTo(ox,      oy + oh,  ox,      oy,      radius);
        ctx.arcTo(ox,      oy,       ox + ow, oy,      radius);
        ctx.closePath();
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
    };
    draw();

    // 4) Video track from canvas
    const canvasStream = canvas.captureStream(30);
    const [videoTrack] = canvasStream.getVideoTracks();

    // 5) Audio mix: screen tab audio + mic → single MediaStream via WebAudio
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    const dest = audioCtx.createMediaStreamDestination();
    for (const src of [screenStream, micStream]) {
      if (!src) continue;
      if (src.getAudioTracks().length === 0) continue;
      try {
        const node = audioCtx.createMediaStreamSource(new MediaStream(src.getAudioTracks()));
        node.connect(dest);
      } catch { /* noop */ }
    }

    // 6) Combine composite video + mixed audio into the final outgoing stream
    const out = new MediaStream();
    out.addTrack(videoTrack);
    dest.stream.getAudioTracks().forEach(t => out.addTrack(t));

    // 7) Stash refs so we can stop cleanly
    compositeRef.current = {
      canvas, ctx, screenVideo, camVideo, camStream, screenStream, micStream, audioCtx,
      rafId: rafId,
      stop: () => {
        stopped = true;
        try { cancelAnimationFrame(rafId); } catch { /* noop */ }
        [screenStream, camStream, micStream].forEach(st => { try { st?.getTracks().forEach(t => t.stop()); } catch { /* noop */ } });
        try { audioCtx.close(); } catch { /* noop */ }
      },
    };

    // 8) Auto-end when the streamer stops sharing their screen via the picker
    screenStream.getVideoTracks()[0].onended = () => {
      if (streamRef.current === out) stopStream();
    };

    return out;
  };

  // Snapshot loop — captures the outgoing stream to a small JPEG every ~5s
  // and POSTs it to /api/channels/live-snapshot so the landing hero widget
  // can render a real (slightly-delayed) frame of the live video.
  const startSnapshotLoop = () => {
    stopSnapshotLoop(); // safety
    if (!streamRef.current) {
      // eslint-disable-next-line no-console
      console.warn("[MystiCast] snapshot loop: no local stream, skipping");
      return;
    }
    // Chromium/Safari won't render frames for a detached <video>. Attach a
    // 1×1 hidden element to the document so the media pipeline actually runs.
    const video = document.createElement("video");
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("aria-hidden", "true");
    Object.assign(video.style, {
      position: "fixed", left: "-9999px", top: "-9999px",
      width: "1px", height: "1px", opacity: "0", pointerEvents: "none",
    });
    document.body.appendChild(video);
    try { video.srcObject = streamRef.current; } catch { /* noop */ }
    try { video.play().catch(() => {}); } catch { /* noop */ }

    const canvas = document.createElement("canvas");
    canvas.width = 320;
    canvas.height = 180;
    snapshotRef.current = { video, canvas, timer: null, attempts: 0 };

    const captureOnce = async () => {
      const s = snapshotRef.current;
      if (!s) return;
      s.attempts += 1;
      try {
        const v = s.video;
        const c = s.canvas;
        if (!v || !c) return;
        if (v.readyState < 2 || v.videoWidth === 0) {
          if (s.attempts <= 6) {
            // eslint-disable-next-line no-console
            console.debug("[MystiCast] snapshot: video not ready yet", { readyState: v.readyState, w: v.videoWidth });
          }
          return;
        }
        const ctx = c.getContext("2d");
        const vw = v.videoWidth, vh = v.videoHeight;
        const cw = c.width, ch = c.height;
        const scale = Math.max(cw / vw, ch / vh);
        const sw = cw / scale, sh = ch / scale;
        const sx = (vw - sw) / 2, sy = (vh - sh) / 2;
        ctx.drawImage(v, sx, sy, sw, sh, 0, 0, cw, ch);
        const dataUrl = c.toDataURL("image/jpeg", 0.55);
        if (dataUrl && dataUrl.length < 45_000) {
          try {
            await api.post("/channels/live-snapshot", { data_url: dataUrl });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[MystiCast] snapshot POST failed:", {
              status: e?.response?.status,
              data: e?.response?.data,
              message: e?.message,
              code: e?.code,
              url: e?.config?.url,
              size: dataUrl.length,
            });
          }
        } else if (dataUrl) {
          // eslint-disable-next-line no-console
          console.warn("[MystiCast] snapshot too large, skipping:", dataUrl.length, "bytes");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[MystiCast] snapshot capture error:", err);
      }
    };
    // Try quickly after ~1s (video needs a moment to warm up), then every 5s.
    setTimeout(captureOnce, 1000);
    // Also retry a couple of times in the first 5 seconds so we don't stall
    // waiting for the very first frame.
    setTimeout(captureOnce, 2500);
    setTimeout(captureOnce, 4000);
    snapshotRef.current.timer = setInterval(captureOnce, 5000);
  };

  const stopSnapshotLoop = () => {
    const s = snapshotRef.current;
    if (!s) return;
    if (s.timer) clearInterval(s.timer);
    try {
      if (s.video) {
        s.video.srcObject = null;
        s.video.remove?.();
      }
    } catch { /* noop */ }
    snapshotRef.current = { timer: null, canvas: null, video: null };
  };

  const startStream = async (ch, opts = {}) => {
    if (!ch) throw new Error("channel required");
    if (isLive) return; // already live
    setStarting(true);
    closingRef.current = false;
    try {
      const { cameraId, micId, screen = false, withCamera = false } = opts;
      let s;
      const composite = screen && withCamera;
      if (composite) {
        s = await buildComposite({ cameraId, micId });
        setPipMode(true);
      } else if (screen) {
        s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: micId ? { deviceId: { exact: micId } } : true, video: false });
          mic.getAudioTracks().forEach(t => s.addTrack(t));
        } catch { /* noop */ }
      } else {
        try {
          s = await navigator.mediaDevices.getUserMedia({
            video: cameraId ? { deviceId: { exact: cameraId }, width: { ideal: 1280 }, height: { ideal: 720 } } : { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: micId    ? { deviceId: { exact: micId } } : true,
          });
        } catch (err) {
          if (err && (err.name === "OverconstrainedError" || err.name === "NotFoundError") && (cameraId || micId)) {
            s = await navigator.mediaDevices.getUserMedia({
              video: { width: { ideal: 1280 }, height: { ideal: 720 } },
              audio: true,
            });
          } else {
            throw err;
          }
        }
      }
      streamRef.current = s;
      setCamOn(true); setMicOn(true); setScreenShare(!!screen);
      applyStreamToPreviews(s);

      // Fire up MediaRecorder for this session. Best-effort — if the browser
      // doesn't support MediaRecorder or webm, we still let the stream go
      // live and just skip recording.
      try {
        chunksRef.current = [];
        const mimes = [
          "video/webm;codecs=vp9,opus",
          "video/webm;codecs=vp8,opus",
          "video/webm",
        ];
        const mime = mimes.find(m => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m));
        if (!mime) {
          setRecordingSupported(false);
        } else {
          setRecordingSupported(true);
          const rec = new MediaRecorder(s, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
          rec.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
          };
          rec.onerror = (ev) => {
            // Log but don't crash — the live stream is more important than the recording.
            // eslint-disable-next-line no-console
            console.warn("[Recording] MediaRecorder error", ev);
          };
          // 2-second chunk cadence — keeps memory reasonable and gives us a
          // recording even if the recorder dies mid-stream.
          rec.start(2000);
          recorderRef.current = rec;
          recordStartRef.current = Date.now();
        }
      } catch (recErr) {
        // eslint-disable-next-line no-console
        console.warn("[Recording] MediaRecorder init failed:", recErr);
        setRecordingSupported(false);
      }

      // If a track ends (user closed the screen-share dialog or unplugged the
      // camera), gracefully end the stream.
      s.getTracks().forEach(t => {
        t.onended = () => {
          if (streamRef.current === s) stopStream();
        };
      });

      const jwt = localStorage.getItem("sc_token");
      const sock = await openWs(ch, jwt);
      wsRef.current = sock;
      setChannelUsername(ch);
      setIsLive(true);
      // Small delay to let the room presence settle before announcing live
      setTimeout(() => { try { sock.send(JSON.stringify({ type: "streamer_live" })); } catch { /* noop */ } }, 50);
      // Kick off the snapshot loop so the landing hero widget can preview us.
      startSnapshotLoop();
      toast.success(screen ? "You're live — screen sharing" : "You're live — say hi!", {
        description: "Your stream stays alive when you navigate around the app.",
      });
    } catch (e) {
      // Roll back any partial state on error
      try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
      setIsLive(false);
      setChannelUsername(null);
      throw e;
    } finally {
      setStarting(false);
    }
  };

  const stopStream = () => {
    closingRef.current = true;
    stopSnapshotLoop();
    // Cancel any pending reconnect timer
    if (reconnectRef.current.timer) {
      clearTimeout(reconnectRef.current.timer);
      reconnectRef.current.timer = null;
    }
    reconnectRef.current.attempts = 0;
    // Finalize recording (if any) BEFORE we stop the media tracks. We wrap
    // the recorder's onstop event in a Promise-like sync callback so the
    // final chunk is captured. The blob is exposed as `pendingRecording`
    // which surfaces the review modal (Save / Download / Delete).
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        const chan = channelUsername;
        // Capture the recording start time NOW — the onstop callback fires
        // asynchronously, and we reset recordStartRef right below. If we read
        // it inside onstop, it'll be null and duration comes out as 0:00.
        const startedAt = recordStartRef.current;
        rec.onstop = () => {
          try {
            const chunks = chunksRef.current || [];
            if (chunks.length > 0) {
              const type = chunks[0]?.type || "video/webm";
              const blob = new Blob(chunks, { type });
              const url = URL.createObjectURL(blob);
              const durationMs = startedAt ? Date.now() - startedAt : 0;
              setPendingRecording({
                blob,
                url,
                mime: type,
                sizeBytes: blob.size,
                durationMs,
                channelUsername: chan,
                createdAt: new Date().toISOString(),
              });
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[Recording] finalize failed:", e);
          }
          chunksRef.current = [];
        };
        rec.stop();
      } catch { /* noop */ }
    }
    recorderRef.current = null;
    recordStartRef.current = null;
    // Announce offline
    sendWs({ type: "streamer_offline" });
    // Close peer connections
    Object.values(peersRef.current).forEach(pc => { try { pc.close(); } catch { /* noop */ } });
    peersRef.current = {};
    // Stop tracks
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
    }
    // Tear down the picture-in-picture compositor if it was running
    if (compositeRef.current) {
      try { compositeRef.current.stop(); } catch { /* noop */ }
      compositeRef.current = null;
      setPipMode(false);
    }
    previewElsRef.current.forEach(el => { try { el.srcObject = null; } catch { /* noop */ } });
    // Close WS
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
    setIsLive(false);
    setChannelUsername(null);
    setScreenShare(false);
    setViewerCount(0);
    setPeerId(null);
    setWsReady(false);
  };

  const toggleCam = () => {
    if (!streamRef.current) return;
    const enabled = !camOn;
    streamRef.current.getVideoTracks().forEach(t => (t.enabled = enabled));
    setCamOn(enabled);
  };
  const toggleMic = () => {
    if (!streamRef.current) return;
    const enabled = !micOn;
    streamRef.current.getAudioTracks().forEach(t => (t.enabled = enabled));
    setMicOn(enabled);
  };

  // If the user signs out while broadcasting, tear everything down.
  useEffect(() => {
    if (!user && (isLive || streamRef.current)) {
      stopStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Warn the user before an accidental full page reload / tab close while live.
  useEffect(() => {
    if (!isLive) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "You're live — leaving this tab will end your stream.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isLive]);

  const dismissPendingRecording = () => {
    setPendingRecording((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch { /* noop */ }
      }
      return null;
    });
  };

  const value = {
    isLive,
    channelUsername,
    peerId,
    starting,
    camOn,
    micOn,
    screenShare,
    viewerCount,
    wsReady,
    ws: wsRef.current,
    stream: streamRef.current,
    startStream,
    stopStream,
    toggleCam,
    toggleMic,
    attachPreview,
    subscribeMessages,
    sendWs,
    pendingRecording,
    dismissPendingRecording,
    recordingSupported,
    guests,
    // Picture-in-picture (screen + camera compositor)
    pipMode,
    cameraOverlay,
    setCameraOverlay,
  };

  return <StreamingCtx.Provider value={value}>{children}</StreamingCtx.Provider>;
}

export const useStreaming = () => useContext(StreamingCtx) || {
  isLive: false, channelUsername: null, peerId: null, starting: false,
  camOn: true, micOn: true, screenShare: false, viewerCount: 0, wsReady: false,
  ws: null, stream: null,
  startStream: async () => {}, stopStream: () => {}, toggleCam: () => {}, toggleMic: () => {},
  attachPreview: () => () => {}, subscribeMessages: () => () => {}, sendWs: () => false,
  pendingRecording: null, dismissPendingRecording: () => {}, recordingSupported: false,
  guests: {},
  pipMode: false, cameraOverlay: { x: 0.7, y: 0.7, w: 0.28, h: 0.28, mirror: true }, setCameraOverlay: () => {},
};
